import { Button, Div, DivProps, P } from '@kizi/core-components';
import {
  IconV2 as Icon,
  IconV2Props,
  Rating,
  ProsConsCompare,
} from '@kizi/shared-components';
import React, { useEffect, useRef, useState } from 'react';
import { withFormProps } from './HOCs/withFormProps';
import { TruncatedText } from './TruncatedText';
import { ReviewCriteriaTable } from './ReviewCriteriaTable';
import { Testimonial, Recommendations } from './hooks/reviews';
export interface HelpfulVotes {
  votes: number;
  label: string;
  onHelpfulVotesClick?: () => {};
}
export interface ReviewCardProps extends Omit<DivProps, 'children'> {
  testimonial: Testimonial;
  iconName?: string;
  iconProps?: IconV2Props;
  isExpandable?: boolean;
  showLess?: string;
  showMore?: string;
  showReviewCriteria?: string;
  hideReviewCriteria?: string;
  helpfulVotes?: HelpfulVotes;
}

const FormRating = withFormProps(Rating);
const TRUNCATED_REVIEW_HEIGHT = 40;

export const ReviewCard: React.FC<ReviewCardProps> = React.forwardRef<
  HTMLDivElement,
  ReviewCardProps
>(
  (
    {
      testimonial,
      iconName = 'finger',
      iconProps,
      isExpandable = false,
      showLess = 'Verberg',
      showMore = 'Lees meer',
      showReviewCriteria = 'Toon meer',
      hideReviewCriteria = 'Verberg beoordeling per onderdeel',
      helpfulVotes = {},
      ...divProps
    },
    ref
  ) => {
    const reviewRef = useRef<HTMLInputElement>(null);

    const [hasShowMoreButton, setHasShowMoreButton] = useState(false);
    const [isReviewsOpen, setIsReviewsOpen] = useState(false);
    const [_helpfulVotes, setHelpfulVotes] = useState(helpfulVotes?.votes || 0);

    const hasExpandableRatings = isExpandable && testimonial.ratings.length > 0;
    const reviewCriteriaButton = isReviewsOpen
      ? hideReviewCriteria
      : showReviewCriteria;

    const hasHelpfulVotesComponent = Object.keys(helpfulVotes).length > 0;

    const helpfulVotesClick = helpfulVotes?.onHelpfulVotesClick;
    const helpfulVotesClickHandler = () => {
      setHelpfulVotes(_helpfulVotes + 1);
      helpfulVotesClick && helpfulVotesClick();
    };

    let reviewHeight;

    const handleReviewHeight = (elementHeight, reference) => {
      if (elementHeight > reference) {
        setHasShowMoreButton(true);
      }
    };

    useEffect(() => {
      const reviewParagraph = reviewRef.current;
      reviewHeight = reviewParagraph?.getBoundingClientRect().height;
      handleReviewHeight(reviewHeight, TRUNCATED_REVIEW_HEIGHT);
    }, []);

    const {
      title,
      averageScore,
      ratings,
      userName,
      publicationDate,
      pros,
      cons,
      description,
    } = testimonial;

    const formatedDate = new Date(publicationDate).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return (
      <Div
        display="flex"
        flexDirection="column"
        ref={ref}
        variant="borderCard"
        {...divProps}
      >
        {testimonial.recommendation === Recommendations.RECOMMENDED && (
          <Div alignItems="flex-end" display="flex" mb={3}>
            <Icon
              mr={2}
              name={iconName}
              variant="successSmall"
              {...iconProps}
            />
            <P textAlign="center" variant="captionRegularSmall">
              Ik beveel dit product aan
            </P>
          </Div>
        )}
        {title && (
          <P variant="footnoteImportant" mb={3}>
            {title}
          </P>
        )}
        <Div display="flex" mb={3}>
          <FormRating
            disabled
            disabledColor="rating"
            iconProps={{ name: 'star', size: 13, fill: 'rating' }}
            value={averageScore / 2}
            _labelProps={{ m: '0' }}
          />
          {hasExpandableRatings && (
            <Button
              ml={3}
              onClick={(e) => {
                e.stopPropagation();
                setIsReviewsOpen(!isReviewsOpen);
              }}
              variant="transparentExtraSmallImportant"
            >
              {reviewCriteriaButton}
            </Button>
          )}
        </Div>
        {isReviewsOpen && <ReviewCriteriaTable mb={3} ratings={ratings} />}

        <Div display="flex" mb={3}>
          <P mr={3} variant="captionRegularSmall">
            {formatedDate}
          </P>
          <P variant="captionRegularSmall">{userName}</P>
        </Div>
        {isExpandable && <ProsConsCompare mb={3} cons={cons} pros={pros} />}
        <TruncatedText
          lines={2}
          showLess={showLess}
          showMore={showMore}
          hasShowMoreButton={hasShowMoreButton}
          variant="truncatedReview"
        >
          <P display="inline" ref={reviewRef} variant="footnoteRegular">
            {description}
          </P>
        </TruncatedText>
        {isExpandable && hasHelpfulVotesComponent && (
          <Div display="flex" mt={3}>
            <P mr={2} variant="captionImportant">
              {helpfulVotes.label}
            </P>
            <Button
              display="flex"
              onClick={(e) => {
                e.stopPropagation();
                helpfulVotesClickHandler();
              }}
              variant="transparentSmall"
            >
              <Icon mr="2px" name="finger" variant="infoExtraSmall" />
              <P variant="captionRegularSmallInfo"> ({_helpfulVotes})</P>
            </Button>
          </Div>
        )}
      </Div>
    );
  }
);
